import { useState } from 'react';
import {
    Folder,
    FolderOpen,
    Move,
    Archive,
    Image,
    Pencil,
    ArrowUpDown,
    Download,
    Eye,
    FileCheck,
    MapPin,
    Loader2,
    ClipboardList,
    MoreVertical,
    Trash2,
    ChevronDown,
    RotateCcw,
    Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils/utils';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

/**
 * Combobox de sélection d'utilisateur avec barre de recherche
 */
function UserCombobox({ users, value, onSelect, disabled, selectedUser }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="plain"
                    size="auto"
                    color="neutral"
                    disabled={disabled}
                    className="h-8 border-0 bg-transparent shadow-none hover:bg-surface-hover focus:ring-0 px-2 -ml-2 justify-between gap-1 text-sm font-normal max-w-[160px]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <span className="truncate">{selectedUser ? selectedUser.name : t('folders:nonAssigne')}</span>
                    <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start" onClick={(e) => e.stopPropagation()}>
                <Command>
                    <CommandInput placeholder={t('folders:rechercher')} />
                    <CommandList>
                        <CommandEmpty>{t('folders:aucunResultat')}</CommandEmpty>
                        {users.map((user) => (
                            <TooltipProvider key={user.id} delayDuration={300}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <CommandItem
                                            value={`${user.name} ${user.companyName || ''}`}
                                            onSelect={() => {
                                                onSelect(String(user.id));
                                                setOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    String(value) === String(user.id) ? 'opacity-100' : 'opacity-0',
                                                )}
                                            />
                                            <span className="truncate">
                                                {user.name || String(user.id)}
                                                {user.companyName ? ` (${user.companyName})` : ''}
                                            </span>
                                        </CommandItem>
                                    </TooltipTrigger>
                                    {(user.email || user.companyName) && (
                                        <TooltipContent side="right">
                                            <div className="flex flex-col text-xs">
                                                {user.email && <span>{user.email}</span>}
                                                {user.companyName && <span>{user.companyName}</span>}
                                            </div>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

/**
 * Crée les définitions de colonnes pour la table des dossiers
 *
 * @param {Object} options
 * @param {number} options.depth - Niveau de profondeur actuel (0 = racine)
 * @param {Array} options.users - Liste des utilisateurs pour l'assignation
 * @param {function} options.onEdit - Callback pour éditer un élément
 * @param {function} options.onAssignUser - Callback pour assigner un utilisateur
 * @param {function} options.onNavigate - Callback pour naviguer dans un dossier
 * @param {function} options.onOpenPhoto - Callback pour ouvrir une photo
 * @param {function} options.onDownloadPhoto - Callback pour télécharger une photo
 * @param {function} options.onViewCertificate - Callback pour voir le certificat
 * @returns {import('@tanstack/react-table').ColumnDef[]}
 */
export function createFoldersColumns({
    depth = 0,
    users = [],
    isSuperAdmin = false,
    adminTemplateMap,
    onEdit,
    onMove,
    canMoveItem,
    onAssignUser,
    assigningFolderId,
    onNavigate,
    onOpenPhoto,
    onDownloadPhoto,
    onViewCertificate,
    onArchivePhoto,
    onArchiveItem,
    onRestoreItem,
    onStatusChange,
}) {
    return [
        {
            accessorKey: 'name',
            header: ({ column }) => (
                <Button
                    variant="plain"
                    color="neutral"
                    size="auto"
                    onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                    className="w-full justify-between! hover:bg-transparent"
                >
                    {i18n.t('folders:nom')}
                    <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                </Button>
            ),
            cell: ({ row }) => {
                const item = row.original;

                // Skeleton de chargement
                if (item.type === 'loading_skeleton') {
                    return (
                        <div className="flex items-center gap-3 min-w-0">
                            <Skeleton className="h-10 w-10 rounded-md shrink-0" />
                            <div className="flex flex-col gap-1.5 flex-1">
                                <div className="flex items-center gap-2">
                                    <Skeleton className="h-4 w-40" />
                                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                </div>
                                <Skeleton className="h-3 w-28" />
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="shrink-0">
                            <ItemIcon item={item} />
                        </div>
                        <div className="flex flex-col items-start gap-1 min-w-0 overflow-hidden">
                            <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="truncate cursor-default">{item.name}</span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs break-all">
                                        {item.name}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            {item.type === 'procedure_report' &&
                                item.procedureTemplateId &&
                                adminTemplateMap?.has(item.procedureTemplateId) && (
                                    <span className="text-[10px] font-medium text-badge-invitation bg-badge-invitation/10 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">
                                        {adminTemplateMap.get(item.procedureTemplateId)}
                                    </span>
                                )}
                        </div>
                    </div>
                );
            },
            size: 300,
        },
        {
            accessorKey: 'assignedTo',
            meta: { hideOnMobile: true },
            header: () => <span>{i18n.t('folders:responsable')}</span>,
            cell: ({ row }) => {
                const item = row.original;

                if (depth === 0 && item.type === 'folder' && !item.isLocked && !item.isGalleryFolder) {
                    if (!onAssignUser) {
                        return <span className="text-sm text-muted-foreground">{item.realizedBy || '—'}</span>;
                    }
                    const isAssigning = String(assigningFolderId) === String(item.id) && assigningFolderId != null;
                    const selectedUser = (Array.isArray(users) ? users : []).find(
                        (u) => String(u.id) === String(item.assignedTo),
                    );
                    return (
                        <div className="flex items-center gap-2">
                            <UserCombobox
                                users={Array.isArray(users) ? users : []}
                                value={item.assignedTo || ''}
                                onSelect={(value) => onAssignUser(item.id, value)}
                                disabled={isAssigning}
                                selectedUser={selectedUser}
                            />
                            {isAssigning && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                        </div>
                    );
                }

                return <span className="text-sm text-muted-foreground">{item.realizedBy || '—'}</span>;
            },
            size: 180,
        },
        {
            accessorKey: 'createdByName',
            meta: { hideOnMobile: true },
            header: () => <span>{i18n.t('folders:creePar')}</span>,
            cell: ({ row }) => {
                const item = row.original;
                const name = item.createdByName || '—';
                const email = item.createdByEmail;
                if (!email) {
                    return <span className="text-sm text-muted-foreground">{name}</span>;
                }
                return (
                    <TooltipProvider delayDuration={300}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="text-sm text-muted-foreground cursor-default">{name}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{email}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
            },
            size: 150,
        },
        ...(isSuperAdmin
            ? [
                  {
                      accessorKey: 'companyName',
                      meta: { hideOnMobile: true },
                      header: () => <span>{i18n.t('folders:entreprise')}</span>,
                      cell: ({ row }) => (
                          <span className="text-sm text-muted-foreground">{row.original.companyName || '—'}</span>
                      ),
                      size: 150,
                  },
              ]
            : []),
        {
            accessorKey: 'updatedAt',
            meta: { hideOnMobile: true },
            sortingFn: (rowA, rowB) => {
                const a = rowA.original.updatedAtRaw || '';
                const b = rowB.original.updatedAtRaw || '';
                return a < b ? -1 : a > b ? 1 : 0;
            },
            header: ({ column }) => (
                <Button
                    variant="plain"
                    color="neutral"
                    size="auto"
                    onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                    className="w-full justify-between! hover:bg-transparent"
                >
                    {i18n.t('folders:derniereModif')}
                    <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                </Button>
            ),
            cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.updatedAt || '—'}</span>,
            size: 120,
        },
        {
            accessorKey: 'createdAt',
            meta: { hideOnMobile: true },
            sortingFn: (rowA, rowB) => {
                const a = rowA.original.createdAtRaw || '';
                const b = rowB.original.createdAtRaw || '';
                return a < b ? -1 : a > b ? 1 : 0;
            },
            header: ({ column }) => (
                <Button
                    variant="plain"
                    color="neutral"
                    size="auto"
                    onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                    className="w-full justify-between! hover:bg-transparent"
                >
                    {i18n.t('folders:creeLe')}
                    <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                </Button>
            ),
            cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.createdAt || '—'}</span>,
            size: 120,
        },
        {
            accessorKey: 'status',
            meta: { hideOnMobile: true },
            header: () => <span>{i18n.t('folders:statut')}</span>,
            cell: ({ row }) => {
                const item = row.original;
                if (item.isArchived) {
                    return <StatusBadge item={item} />;
                }
                if (item.type === 'folder' || item.type === 'intervention') {
                    return <FolderStatusSelect item={item} onStatusChange={onStatusChange} />;
                }
                return <StatusBadge item={item} />;
            },
            size: 120,
        },
        {
            accessorKey: 'tokenConsumption',
            meta: { hideOnMobile: true, className: 'text-right' },
            header: () => <span className="text-right block">{i18n.t('folders:consoJetons')}</span>,
            cell: ({ row }) => {
                const value = row.original.tokenConsumption;
                if (value === null || value === undefined) return null;
                return (
                    <span className="text-sm text-muted-foreground tabular-nums block text-right">{value ?? 0}</span>
                );
            },
            size: 100,
        },
        {
            id: 'actions',
            header: () => <span className="text-right block">{i18n.t('folders:actions')}</span>,
            cell: ({ row }) => {
                const item = row.original;

                // Actions pour les dossiers et sous-dossiers
                if (item.type !== 'photo' && !item.isLocked && !item.isGalleryFolder) {
                    const canEdit = onEdit && item.type !== 'procedure_report';
                    const canMove =
                        onMove &&
                        item.type !== 'folder' &&
                        (typeof canMoveItem === 'function' ? canMoveItem(item) : true);
                    const canArchive = onArchiveItem && !item.isArchived && item.type !== 'procedure_report';
                    const canRestore = onRestoreItem && item.isArchived;

                    if (!canEdit && !canMove && !canArchive && !canRestore) return null;

                    return (
                        <div className="flex justify-end">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="xsm"
                                        className="text-muted-foreground hover:text-foreground h-8 px-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <MoreVertical className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                    {canMove && (
                                        <DropdownMenuItem onClick={() => onMove(item)}>
                                            <Move className="w-4 h-4 mr-2" />
                                            {i18n.t('folders:deplacer')}
                                        </DropdownMenuItem>
                                    )}
                                    {canEdit && (
                                        <DropdownMenuItem onClick={() => onEdit(item)}>
                                            <Pencil className="w-4 h-4 mr-2" />
                                            {i18n.t('folders:modifier')}
                                        </DropdownMenuItem>
                                    )}
                                    {canArchive && (
                                        <>
                                            {(canEdit || canMove) && <DropdownMenuSeparator />}
                                            <DropdownMenuItem
                                                onClick={() => onArchiveItem(item)}
                                                className="text-destructive focus:text-destructive"
                                            >
                                                <Archive className="w-4 h-4 mr-2" />
                                                {i18n.t('folders:archiver')}
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    {canRestore && (
                                        <>
                                            {(canEdit || canMove) && <DropdownMenuSeparator />}
                                            <DropdownMenuItem onClick={() => onRestoreItem(item)}>
                                                <RotateCcw className="w-4 h-4 mr-2" />
                                                {i18n.t('folders:restaurer')}
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    );
                }

                // Actions pour les photos
                if (item.type === 'photo') {
                    const hasGps = item.latitude && item.longitude;
                    const canMovePhoto = onMove && (typeof canMoveItem === 'function' ? canMoveItem(item) : true);
                    const googleMapsUrl = hasGps
                        ? `https://www.google.com/maps?q=${item.latitude},${item.longitude}`
                        : null;

                    return (
                        <div className="flex justify-end">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="xsm"
                                        className="text-muted-foreground hover:text-foreground h-8 px-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <MoreVertical className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenuItem onClick={() => onOpenPhoto?.(item)}>
                                        <Eye className="w-4 h-4 mr-2" />
                                        {i18n.t('folders:voirLImage')}
                                    </DropdownMenuItem>
                                    {canMovePhoto && (
                                        <DropdownMenuItem onClick={() => onMove(item)}>
                                            <Move className="w-4 h-4 mr-2" />
                                            {i18n.t('folders:deplacer')}
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => onDownloadPhoto?.(item)}>
                                        <Download className="w-4 h-4 mr-2" />
                                        {i18n.t('folders:telecharger')}
                                    </DropdownMenuItem>
                                    {item.certificateId && (
                                        <DropdownMenuItem onClick={() => onViewCertificate?.(item)}>
                                            <FileCheck className="w-4 h-4 mr-2" />
                                            {i18n.t('folders:certificat')}
                                        </DropdownMenuItem>
                                    )}
                                    {hasGps && (
                                        <DropdownMenuItem onClick={() => window.open(googleMapsUrl, '_blank')}>
                                            <MapPin className="w-4 h-4 mr-2" />
                                            {i18n.t('folders:voirSurGoogleMaps')}
                                        </DropdownMenuItem>
                                    )}
                                    {onArchivePhoto && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onClick={() => onArchivePhoto(item)}
                                                className="text-destructive focus:text-destructive"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                {i18n.t('folders:archiver')}
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    );
                }

                return null;
            },
            size: 80,
            enableSorting: false,
            enableHiding: false,
        },
    ];
}

/**
 * Composant d'icône pour un élément (dossier, photo, procédure)
 */
function ItemIcon({ item, className, ...props }) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const item_type = item.type.toLowerCase();

    switch (item_type) {
        case 'folder':
            return (
                <div
                    className={cn(
                        'w-9 h-9 p-2.5 bg-dir-folder-bg rounded-md flex items-center justify-center',
                        className,
                    )}
                    {...props}
                >
                    <Folder className="w-5 h-5 text-dir-folder" />
                </div>
            );
        case 'intervention':
        case 'generic':
        case 'photo_only':
        case 'folder_gallery':
        case 'invitations':
            return (
                <div
                    className={cn(
                        'w-9 h-9 p-2.5 bg-dir-subfolder-bg rounded-md flex items-center justify-center',
                        className,
                    )}
                    {...props}
                >
                    <FolderOpen className="w-5 h-5 text-dir-subfolder" />
                </div>
            );
        case 'photo':
            return (
                <div
                    className={cn(
                        'w-9 h-9 bg-dir-picture-bg rounded-md flex items-center justify-center overflow-hidden relative',
                        className,
                    )}
                    {...props}
                >
                    {item.thumbnailUrl && !hasError ? (
                        <>
                            {/* Placeholder pendant le chargement */}
                            {!isLoaded && <div className="absolute inset-0 bg-surface animate-pulse" />}
                            <img
                                src={item.thumbnailUrl}
                                alt=""
                                className={`w-full h-full object-cover transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                loading="lazy"
                                onLoad={() => setIsLoaded(true)}
                                onError={() => setHasError(true)}
                            />
                        </>
                    ) : (
                        <Image className="w-5 h-5 text-dir-picture" />
                    )}
                </div>
            );
        case 'procedure_report':
            return (
                <div
                    className={cn(
                        'w-9 h-9 p-2.5 bg-dir-report-bg rounded-md flex items-center justify-center',
                        className,
                    )}
                    {...props}
                >
                    <ClipboardList className="w-5 h-5 text-dir-report" />
                </div>
            );
        default:
            return null;
    }
}

function getFolderStatusOptions() {
    return [
        { value: 'open', label: i18n.t('folders:ouvert'), badgeStyle: 'success', menuColor: 'text-status-success' },
        { value: 'closed', label: i18n.t('folders:ferme'), badgeStyle: 'muted', menuColor: 'text-muted-foreground' },
    ];
}

function FolderStatusSelect({ item, onStatusChange }) {
    const { t } = useTranslation();
    const FOLDER_STATUS_OPTIONS = getFolderStatusOptions();
    if (!onStatusChange) {
        // Read-only: show badge or dash
        const opt = FOLDER_STATUS_OPTIONS.find((o) => o.value === item.status);
        if (!opt) return <span className="text-sm text-muted-foreground">—</span>;
        return (
            <span className={`text-[13px] px-2 py-1 rounded-md font-medium ${BADGE_STYLES[opt.badgeStyle]}`}>
                {opt.label}
            </span>
        );
    }

    const current = FOLDER_STATUS_OPTIONS.find((o) => o.value === item.status);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className="inline-flex items-center gap-1.5 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                >
                    <span
                        className={cn(
                            'text-[13px] px-2 py-1 rounded-md font-medium',
                            current ? BADGE_STYLES[current.badgeStyle] : 'text-muted-foreground',
                        )}
                    >
                        {current ? current.label : '—'}
                    </span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                {item.status && (
                    <>
                        <DropdownMenuItem
                            onClick={() => onStatusChange(item, null)}
                            className="text-[13px] text-muted-foreground"
                        >
                            {t('folders:sansStatut')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                    </>
                )}
                {FOLDER_STATUS_OPTIONS.map((opt) => {
                    const isSelected = opt.value === item.status;
                    return (
                        <DropdownMenuItem
                            key={opt.value}
                            onClick={() => !isSelected && onStatusChange(item, opt.value)}
                            className={cn(
                                'text-[13px] font-medium',
                                isSelected ? `${BADGE_STYLES[opt.badgeStyle]} pointer-events-none` : opt.menuColor,
                            )}
                        >
                            {opt.label}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * Composant de badge de statut pour les procédures et les photos (certification)
 */
const BADGE_STYLES = {
    success: 'bg-status-success-bg text-status-success',
    pending: 'bg-status-pending-bg text-status-pending',
    destructive: 'bg-destructive text-destructive-foreground',
    muted: 'bg-surface text-muted-foreground',
};

function StatusBadge({ item }) {
    const { t } = useTranslation();
    let label = null;
    let style = null;

    if (item.type === 'procedure_report' && item.statusLabel) {
        if (item.isArchived) {
            label = `Archivé - ${item.statusLabel}`;
            style = BADGE_STYLES.destructive;
        } else {
            label = item.statusLabel;
            style = item.status === 'completed' ? BADGE_STYLES.success : BADGE_STYLES.pending;
        }
    } else if (item.type === 'photo' && item.certificationStatus) {
        const tag = item.certificationStatus.tag;
        const certLabel = item.certificationStatus.name || t('folders:enCours');
        if (item.isArchived) {
            label = `Archivé - ${certLabel}`;
            style = BADGE_STYLES.destructive;
        } else {
            label = certLabel;
            style =
                tag === 'success'
                    ? BADGE_STYLES.success
                    : tag === 'pending' || tag === 'processing'
                      ? BADGE_STYLES.pending
                      : tag === 'error' || tag === 'failed'
                        ? BADGE_STYLES.destructive
                        : BADGE_STYLES.muted;
        }
    } else if (item.isArchived) {
        label = 'Archivé';
        style = BADGE_STYLES.destructive;
    }

    if (!label) return null;

    return <span className={`text-[13px] px-2 py-1 rounded-md font-medium ${style}`}>{label}</span>;
}

export { ItemIcon, StatusBadge };
